import "../../assets/ContentEditor.css";
import React, { useEffect, useState, useMemo, CSSProperties } from "react";
import { Dialog } from '../dialogs/dialog';
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { ColorPicker } from "primereact/colorpicker";
import { Button } from "primereact/button";
import type { Editor } from "@tiptap/core";
import { normalizeColor } from "../../general/utilities";

interface PanelPropertiesDialogProps {
  editor: Editor;
  visible: boolean;
  onHide: () => void;
}

export const PanelPropertiesDialog: React.FC<PanelPropertiesDialogProps> = ({
  editor,
  visible,
  onHide,
}) => {
  const [borderColor, setBorderColor] = useState("#cccccc");
  const [styleName, setStyleName] = useState("default");
  const [borderWidth, setBorderWidth] = useState(1);
  const [borderStyle, setBorderStyle] = useState("solid");
  const [borderRadius, setBorderRadius] = useState(6);
  const [padding, setPadding] = useState(12);
  const [shadow, setShadow] = useState("none");
  const [align, setAlign] = useState("left");
  const [textAlign, setTextAlign] = useState("left");
  const [overflow, setOverflow] = useState("visible");

  const shadowPresets = [
    { label: "None", value: "none" },
    { label: "Small", value: "sm" },
    { label: "Medium", value: "md" },
    { label: "Large", value: "lg" },
  ];

  useEffect(() => {
    if (!visible) return;

    const nodeType = editor.isActive("editorPanel") ? "editorPanel" : (editor.isActive("collapsible") ? "collapsible" : null);

    if (!nodeType) return;

    const attrs = editor.getAttributes(nodeType);

    const themeStyles = getComputedStyle(document.documentElement);
    const defaultBorder: string = themeStyles.getPropertyValue("--surface-border").trim();

    setStyleName(attrs.styleName ?? "default");
    setBorderColor(normalizeColor(attrs.borderColor ?? defaultBorder) ?? "#888888");
    setBorderWidth(attrs.borderWidth ?? 1);
    setBorderStyle(attrs.borderStyle ?? "solid");
    setBorderRadius(attrs.borderRadius ?? 6);
    setPadding(attrs.padding ?? 12);
    setShadow(attrs.shadow ?? "none");
    setAlign(attrs.align ?? "left");
    setTextAlign(attrs.textAlign ?? "left");
    setOverflow(attrs.overflow ?? "visible");
  }, [visible, editor]);

  let alignStyles: CSSProperties = {};
  if (align === "center") alignStyles = { marginLeft: "auto", marginRight: "auto" };
  else if (align === "right") alignStyles = { marginLeft: "auto" };
  else if (align === "full") alignStyles = { width: "100%" };

  const panelVariants: Record<string, CSSProperties> = {
    default: {
        background: "var(--surface-0)",
        color: "var(--text-color)",
    },
    info: {
        background: "var(--blue-50)",
        color: "var(--blue-900)",
    },
    success: {
        background: "var(--green-50)",
        color: "var(--green-900)",
    },
    warning: {
        background: "var(--yellow-50)",
        color: "var(--yellow-900)",
    },
    danger: {
        background: "var(--red-50)",
        color: "var(--red-900)",
    },
    muted: {
        background: "var(--surface-100)",
        color: "var(--text-color)",
    },
  };

  const previewStyle = useMemo(() => {
    const shadowMap: Record<string, string> = {
      none: "none",
      sm: "var(--p-shadow-sm, 0 1px 2px rgba(0,0,0,0.1))",
      md: "var(--p-shadow-md, 0 4px 6px rgba(0,0,0,0.1))",
      lg: "var(--p-shadow-lg, 0 10px 15px rgba(0,0,0,0.15))",
    };

    return {
      ...panelVariants[styleName],
      borderColor,
      borderWidth: `${borderWidth}px`,
      borderStyle: borderStyle as CSSProperties["borderStyle"],
      borderRadius: `${borderRadius}px`,
      padding: `${padding}px`,
      boxShadow: shadowMap[shadow],
      textAlign: textAlign as CSSProperties["textAlign"],
      overflow: overflow as CSSProperties["overflow"],
      minHeight: "60px",
      ...alignStyles,
    };
  }, [
    styleName,
    borderColor,
    borderWidth,
    borderStyle,
    borderRadius,
    padding,
    shadow,
    align,
    textAlign,
    overflow,
  ]);

  const apply = () => {
    const nodeType = editor.isActive("editorPanel") ? "editorPanel" : (editor.isActive("collapsible") ? "collapsible" : null);

    if (!nodeType) return;

    editor
      .chain()
      .focus()
      .updateAttributes(nodeType, {
        styleName,
        borderColor,
        borderWidth,
        borderStyle,
        borderRadius,
        padding,
        shadow: shadow,
        align,
        textAlign,
        overflow,
      })
      .run();

    onHide();
  };

  const num = { marginLeft: '20px' };
  const dd = { marginLeft: '20px' };

  const alignButtons = [
    { value: "left", icon: "pi pi-align-left" },
    { value: "center", icon: "pi pi-align-center" },
    { value: "right", icon: "pi pi-align-right" },
    { value: "full", icon: "pi pi-arrows-h" },
  ];

  return (
    <Dialog
      header="Panel properties"
      visible={visible}
      onHide={onHide}
      modal
      style={{ width: "800px" }}
    >
    <table border={0} width="100%" cellPadding={10}>
        <tr>
            <td style={{ whiteSpace: "nowrap"}}>
                <label className="p-d-block">Style</label>
                <Dropdown
                    value={styleName}
                    scrollHeight={'300px'}
                    options={[
                        { label: "Default", value: "default" },
                        { label: "Info", value: "info" },
                        { label: "Success", value: "success" },
                        { label: "Warning", value: "warning" },
                        { label: "Danger", value: "danger" },
                        { label: "Muted", value: "muted" },
                    ]}
                    onChange={(e) => setStyleName(e.value)}
                    style={{ width: "200px", marginLeft: "20px" }}
                />
            </td>
            <td style={{ whiteSpace: "nowrap"}}>
                <label className="p-d-block">Border color</label>
                    <ColorPicker
                        format="hex"
                        value={borderColor}
                        onChange={(e) => setBorderColor(`#${e.value}`)}
                        style={{ width: "60px", paddingLeft: '20px' }}
                    />
            </td>
        </tr>
        <tr>
            <td style={{ whiteSpace: "nowrap"}}>
                <label className="p-d-block">Border width (px)</label>
                <InputNumber
                    value={borderWidth}
                    onValueChange={(e) => setBorderWidth(e.value ?? 1)}
                    showButtons
                    min={0}
                    max={20}
                    size={3}
                    incrementButtonClassName="p-button-outlined p-button-sm"
                    decrementButtonClassName="p-button-outlined p-button-sm"
                    style={num}
                />
            </td>
            <td style={{ whiteSpace: "nowrap"}}>
                <label className="p-d-block">Border style</label>
                <Dropdown
                    value={borderStyle}
                    options={[
                    { label: "Solid", value: "solid" },
                    { label: "Dashed", value: "dashed" },
                    { label: "Dotted", value: "dotted" },
                    { label: "None", value: "none" },
                    ]}
                    size={6}
                    onChange={(e) => setBorderStyle(e.value)}
                    style={dd}
                />
            </td>
        </tr>
        <tr>
            <td style={{ whiteSpace: "nowrap"}}>
                <label className="p-d-block">Corner radius (px)</label>
                <InputNumber
                    value={borderRadius}
                    onValueChange={(e) => setBorderRadius(e.value ?? 0)}
                    showButtons
                    min={0}
                    max={50}
                    size={3}
                    incrementButtonClassName="p-button-outlined p-button-sm"
                    decrementButtonClassName="p-button-outlined p-button-sm"
                    style={num}
                />
            </td>
            <td style={{ whiteSpace: "nowrap"}}>
                <label className="p-d-block">Padding (px)</label>
                <InputNumber
                    value={padding}
                    onValueChange={(e) => setPadding(e.value ?? 0)}
                    showButtons
                    min={0}
                    max={100}
                    size={3}
                    incrementButtonClassName="p-button-outlined p-button-sm"
                    decrementButtonClassName="p-button-outlined p-button-sm"
                    style={num}
                />
            </td>
        </tr>
        <tr>
            <td style={{ whiteSpace: "nowrap"}}>
                <label className="p-d-block">Shadow</label>
                <Dropdown
                    value={shadow}
                    options={shadowPresets}
                    onChange={(e) => setShadow(e.value)}
                    style={dd}
                />
            </td>
            <td style={{ whiteSpace: "nowrap"}}>
                <label className="p-d-block">Overflow</label>
                <Dropdown
                    value={overflow}
                    options={[
                    { label: "Visible", value: "visible" },
                    { label: "Hidden", value: "hidden" },
                    { label: "Auto", value: "auto" },
                    { label: "Scroll", value: "scroll" },
                    ]}
                    onChange={(e) => setOverflow(e.value)}
                    style={dd}
                />
            </td>
        </tr>
        <tr>
            <td style={{ whiteSpace: "nowrap"}}>
                <label className="p-d-block">Text align</label>
                <Dropdown
                    value={textAlign}
                    options={[
                    { label: "Left", value: "left" },
                    { label: "Center", value: "center" },
                    { label: "Right", value: "right" },
                    { label: "Justify", value: "justify" },
                    ]}
                    onChange={(e) => setTextAlign(e.value)}
                    style={dd}
                />
            </td>
            <td style={{ whiteSpace: "nowrap"}}>
                <label className="p-d-block">Panel alignment</label>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                    {alignButtons.map((btn) => (
                    <Button
                        key={btn.value}
                        icon={btn.icon}
                        className={
                        align === btn.value ? "p-button-primary" : "p-button-outlined"
                        }
                        onClick={() => setAlign(btn.value)}
                    />
                    ))}
                </div>
            </td>
        </tr>
    </table>

      <div
        style={{
          border: "1px dashed var(--surface-border)",
          padding: "1rem",
          marginBottom: "1.5rem",
        }}
      >
        <div style={previewStyle}>Live preview</div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
        <Button label="Cancel" className="p-button-secondary" onClick={onHide} />
        <Button label="Apply" onClick={apply} />
      </div>
    </Dialog>
  );
};